import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Typography, ButtonBaseProps } from '@material-ui/core';
import MicRoundedIcon from '@material-ui/icons/MicRounded';
import MicOffIcon from '@material-ui/icons/MicOff';
import AddIcon from '@material-ui/icons/Add';
import AddIcCallIcon from '@material-ui/icons/AddIcCall';
import CallEndIcon from '@material-ui/icons/CallEnd';
import DialpadIcon from '@material-ui/icons/Dialpad';
import CallMergeIcon from '@material-ui/icons/CallMerge';
import ClearIcon from '@material-ui/icons/Clear';

import IconBorder from 'components/shared/IconBorder';
import { StyledIconButton } from 'components/shared/MuiExtensions';
import HorizontalContent from 'components/shared/HorizontalContent';

import {
  apiAttemptLiveTransfer,
  apiCancelLiveTransfer,
  apiMergeLiveTransferCall,
  setOutboundRinging,
} from 'state/call.state';
import { RootState } from 'state/store';

import { isLiveTransferRestricted, isWithinBusinessHours, isTeamHoliday } from 'utils/functions';
import { Colors } from 'typings';

import Dialpad from './Dialpad';
import Timer from './Timer';
import { getCommunicationService } from 'services/communication/communication.service.factory';
import CallStream from './CallStream';

type LiveTransferButton = 'add-call' | 'call-customer' | 'cancel-call-customer' | 'merge-calls';

interface CallActionButtonProps extends ButtonBaseProps {
  icon: React.ReactNode;
  label: string;
}

const CallActionButton = ({ icon, label, ...props }: CallActionButtonProps) => {
  return (
    <StyledIconButton {...props}>
      <HorizontalContent>
        <IconBorder boxShadow backgroundColor={props?.disabled ? Colors.MEDIUM_GRAY : undefined}>
          {icon}
        </IconBorder>
        <Typography variant="subtitle1">{label}</Typography>
      </HorizontalContent>
    </StyledIconButton>
  );
};

interface MuteCallButtonProps extends ButtonBaseProps {
  mute: boolean;
}

const MuteCallButton = (props: MuteCallButtonProps) => {
  const { mute, ...buttonProps } = props;
  const MicIcon = mute ? MicOffIcon : MicRoundedIcon;

  return (
    <CallActionButton
      icon={<MicIcon htmlColor={Colors.BLACK} />}
      label={mute ? 'unmute' : 'mute'}
      {...buttonProps}
    />
  );
};

const AddCallButton = (buttonProps: ButtonBaseProps) => {
  return (
    <CallActionButton
      icon={<AddIcon htmlColor={Colors.BLACK} />}
      label="add call"
      {...buttonProps}
    />
  );
};

const CallCustomerButton = (buttonProps: ButtonBaseProps) => {
  return (
    <CallActionButton
      icon={<AddIcCallIcon htmlColor={Colors.BLACK} />}
      label="call customer?"
      {...buttonProps}
    />
  );
};

const CancelCallCustomerButton = (buttonProps: ButtonBaseProps) => {
  return (
    <CallActionButton
      icon={<ClearIcon htmlColor={Colors.BLACK} />}
      label="cancel"
      {...buttonProps}
    />
  );
};

const MergeCallsButton = (buttonProps: ButtonBaseProps) => {
  return (
    <CallActionButton
      icon={<CallMergeIcon htmlColor={Colors.BLACK} />}
      label="merge calls"
      {...buttonProps}
    />
  );
};

interface ToggleKeypadButtonProps extends ButtonBaseProps {
  showKeypad: boolean;
}

const ToggleKeypadButton = (props: ToggleKeypadButtonProps) => {
  const { showKeypad, ...buttonProps } = props;

  return (
    <CallActionButton
      icon={<DialpadIcon htmlColor={Colors.BLACK} />}
      label={showKeypad ? 'hide keypad' : 'show keypad'}
      {...buttonProps}
    />
  );
};

const EndCallButton = (buttonProps: ButtonBaseProps) => {
  return (
    <CallActionButton
      icon={<CallEndIcon htmlColor={Colors.DARK_RED} />}
      label="end call"
      {...buttonProps}
    />
  );
};

const Slider = styled('div')`
  &.slider {
    max-height: 0;
    visibility: hidden;
    position: fixed;
  }

  &.slider-open {
    margin: ${(props) => props.theme.spacing(0, -10)};
    position: absolute;
    bottom: 100%;
    visibility: visible;
    transition-property: all;
    transition-duration: 0.3s;
    transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  }
`;

const Wrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

const OnCall = () => {
  const dispatch = useDispatch();
  const isLiveTransferable = useSelector((state: RootState) => {
    const { agent, metadata } = state.lead.entity || {};
    const callForwardingNumber =
      agent?.callForwardingNumber || metadata?.dynamic?.agent?.callForwardingNumber;

    return !!(
      callForwardingNumber &&
      agent &&
      (!isLiveTransferRestricted(agent) || (!isTeamHoliday(agent) && isWithinBusinessHours(agent)))
    );
  });
  const liveTransferSuccess = useSelector((state: RootState) => state.call.liveTransferSuccess);
  const callStream = useSelector((state: RootState) => state.call.callStream);
  const leadCommunicationProvider = useSelector(
    (state: RootState) => state.lead.entity?.communicationProvider
  );
  const [isMuted, setMute] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [visibleButton, setVisibleButton] = useState<LiveTransferButton>('add-call');

  const communicationService = leadCommunicationProvider
    ? getCommunicationService(leadCommunicationProvider)
    : null;

  useEffect(() => {
    if (liveTransferSuccess) {
      setVisibleButton('merge-calls');
    }
  }, [liveTransferSuccess]);

  const toggleMute = () => {
    if (!leadCommunicationProvider) return;

    const muteCall = !isMuted;

    communicationService?.muteCall(muteCall);
    setMute(muteCall);
  };

  const toggleKeypad = () => {
    setShowKeypad(!showKeypad);
  };

  const handleAddCall = () => {
    setVisibleButton('call-customer');
  };

  const handleCallCustomer = () => {
    dispatch(apiAttemptLiveTransfer());
    if (communicationService?.needsRing()) {
      dispatch(setOutboundRinging(true));
    }
    setVisibleButton('cancel-call-customer');
  };

  const handleCancelCallCustomer = () => {
    dispatch(apiCancelLiveTransfer());
    setOutboundRinging(false);
    setVisibleButton('add-call');
  };

  const handleMergeCalls = () => {
    dispatch(apiMergeLiveTransferCall());
    setVisibleButton('add-call');
  };

  const handleEndCall = () => {
    communicationService?.endCall();
  };

  return (
    <Wrapper>
      <MuteCallButton mute={isMuted} onClick={toggleMute} />
      <div>
        <ToggleKeypadButton onClick={toggleKeypad} showKeypad={showKeypad} />
        <Slider className={showKeypad ? 'slider-open' : 'slider'}>
          <Dialpad />
        </Slider>
      </div>
      {visibleButton === 'add-call' && (
        <AddCallButton onClick={handleAddCall} disabled={!isLiveTransferable} />
      )}
      {visibleButton === 'call-customer' && <CallCustomerButton onClick={handleCallCustomer} />}
      {(visibleButton === 'cancel-call-customer' || visibleButton === 'merge-calls') && (
        <CancelCallCustomerButton onClick={handleCancelCallCustomer} />
      )}
      {visibleButton === 'merge-calls' && <MergeCallsButton onClick={handleMergeCalls} />}
      <EndCallButton onClick={handleEndCall} disabled={visibleButton === 'cancel-call-customer'} />
      <Timer />
      <CallStream mediaStream={callStream} />
    </Wrapper>
  );
};

export default OnCall;
